import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  useRegistrationEmailForm,
  useRegistrationRouteState,
} from '../features/registration/model';
import { RegistrationEmailScreen } from '../features/registration/ui';
import {
  type EmployeeProfile,
  getEmployeeProfile,
} from '../shared/api/employee.service';
import { useEmployeeSession } from '../shared/hooks/use-employee-session';

function resolveRegistrationEmailFormKey(
  email?: string,
  updatedAt?: string,
): string {
  return JSON.stringify([updatedAt ?? null, email ?? null]);
}

type RegistrationEmailPageFormProps = {
  initialProfile: EmployeeProfile | null;
};

function RegistrationEmailPageForm({
  initialProfile,
}: RegistrationEmailPageFormProps) {
  const navigate = useNavigate();
  const registrationRoute = useRegistrationRouteState();
  const emailForm = useRegistrationEmailForm({ initialProfile });

  const handleSubmit = async () => {
    if (!emailForm.canSubmit || emailForm.isSubmitting) {
      return;
    }

    const isSubmitted = await emailForm.submit();
    if (!isSubmitted) {
      return;
    }

    const nextPath = registrationRoute.nextImplementedPath;
    if (!nextPath) {
      return;
    }

    navigate(nextPath);
  };

  return (
    <RegistrationEmailScreen
      canSubmit={emailForm.canSubmit}
      email={emailForm.email}
      emailErrorMessage={emailForm.emailErrorMessage}
      isEmailInvalid={emailForm.isEmailInvalid}
      isSubmitting={emailForm.isSubmitting}
      submitError={emailForm.submitError}
      onClearEmail={() => emailForm.setEmail('')}
      onEmailChange={emailForm.setEmail}
      onSubmit={handleSubmit}
    />
  );
}

export function RegistrationEmailPage() {
  const { status } = useEmployeeSession();
  const profileQuery = useQuery({
    enabled: status === 'authenticated',
    queryFn: getEmployeeProfile,
    queryKey: ['employee', 'profile'],
  });
  const profile = profileQuery.data ?? null;

  return (
    <RegistrationEmailPageForm
      key={resolveRegistrationEmailFormKey(profile?.email, profile?.updatedAt)}
      initialProfile={profile}
    />
  );
}
